import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Popconfirm, Select, Space, Table, notification } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import {
  AiChatHistoryConversationsInterface,
  AiChatHistoryConversationUserInterface,
} from '../../../../../common/interfaces/aiChatSettings';
import { AiChatHistoryService, i18n } from '../../../../../common';
import AiChatHistoryComponent from '../../../../../common/components/aiChat/aiChatHistory.component';

function AiChatHistoryHistoryPageComponent() {
  const [aiChatHistory, setAiChatHistory] = useState<AiChatHistoryConversationsInterface[]>([]);

  const [users, setUsers] = useState<AiChatHistoryConversationUserInterface[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<AiChatHistoryConversationsInterface | null>(null);

  useEffect(() => {
    AiChatHistoryService.getConversationsUsers().subscribe((response) => {
      setSelectedUser(response[0].id);
      setUsers(response);
    });
  }, []);

  const loadConversations = (userId: number) => {
    AiChatHistoryService.getConversations(userId).subscribe((response) => {
      setAiChatHistory(response.reverse());
    });
  };

  useEffect(() => {
    if (!selectedUser) return;
    loadConversations(selectedUser);
  }, [selectedUser]);

  const deleteConversation = (conversationId: string) => {
    AiChatHistoryService.deleteConversation(conversationId).subscribe(() => {
      notification['success']({ message: i18n.translate('api.successDelete'), duration: 2 });
      if (selectedConversation?.conversationId === conversationId) {
        setSelectedConversation(null);
      }
      if (selectedUser) loadConversations(selectedUser);
    });
  };

  const columns: ColumnType<AiChatHistoryConversationsInterface>[] = [
    {
      title: '',
      width: 100,
      render: (record: AiChatHistoryConversationsInterface) => (
        <Button type="link" onClick={() => setSelectedConversation(record)}>
          Otvori
        </Button>
      ),
    },
    {
      title: 'Datum',
      key: 'createdAt',
      render: (text: AiChatHistoryConversationsInterface) => {
        const [timestamp] = text.conversationId.split('-');
        const date = new Date(parseInt(timestamp)).toLocaleDateString('hr-HR');
        const time = new Date(parseInt(timestamp)).toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
        return `${date} ${time}`;
      },
    },
    {
      title: 'Razgovor',
      key: 'conversationId',
      render: (text: AiChatHistoryConversationsInterface) => {
        return text.conversationId;
      },
    },
    {
      title: 'Korisnik',
      key: 'user.name',
      render: (text: AiChatHistoryConversationsInterface) => {
        return text.user.name;
      },
    },
    {
      title: 'Uloga',
      key: 'user.roles',
      render: (text: AiChatHistoryConversationsInterface) => {
        return text.user.roles;
      },
    },
    {
      title: '',
      width: 60,
      align: 'center',
      render: (record: AiChatHistoryConversationsInterface) => (
        <Popconfirm
          title={i18n.t('common.recordRemoval')}
          onConfirm={() => deleteConversation(record.conversationId)}
          okText={i18n.t('genericButtons.yes')}
          cancelText={i18n.t('genericButtons.no')}
          placement="left"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div className="ai-chat-settings-page w100-h100">
        <Space style={{ marginBottom: 16 }} wrap={true}>
          {selectedUser && (
            <Select
              style={{ minWidth: 250 }}
              placeholder="Izaberite korisnika"
              showSearch
              defaultValue={selectedUser.toString()}
              onChange={(response) => setSelectedUser(parseInt(response))}
              options={users.map((x) => ({ value: x.id.toString(), label: x.name }))}
              filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          )}
        </Space>
        <Table
          dataSource={aiChatHistory}
          columns={columns}
          pagination={false}
          rowKey={(record) => record.conversationId}
          locale={{
            emptyText: i18n.t('common.noData'),
            triggerDesc: i18n.t('common.sortDesc'),
            triggerAsc: i18n.t('common.sortAsc'),
            cancelSort: i18n.t('common.cancelSort'),
            filterReset: i18n.t('common.reset'),
          }}
        />
      </div>
      {selectedConversation && <AiChatHistoryComponent conversation={selectedConversation} onClose={() => setSelectedConversation(null)} />}
    </>
  );
}

export default AiChatHistoryHistoryPageComponent;
